import type {ReactElement} from 'react';
import {useContext, useRef, useState} from 'react';
import {OnMyDesignSection} from '@Components/color-picker-v2/components/on-my-design-section';
import {BrandsSection} from '@Components/color-picker-v2/components/brands-section';
import {InputSection} from '@Components/color-picker-v2/components/input-section';
import {HexPicker} from '@Components/color-picker-v2/components/hex-picker';
import {ColorOptionTypeSection} from '@Components/color-picker-v2/components/color-option-type-section';
import {ColorTypeTabs} from '@Components/color-picker-v2/components/color-type-tabs/color-type-tabs';
import {GradientStyleSection} from '@Components/color-picker-v2/components/gradient-style-section/gradient-style-section';
import {GradientStopSwatches} from '@Components/color-picker-v2/components/gradient-stop-swatches/gradient-stop-swatches';
import {ColorFill, ColorFillPropertyType} from '@Components/color-fill';
import './color-picker-v2.scss';
import {DefaultPresetSection} from '@Components/color-picker-mobile/components/default-preset-section';
import styles from './color-picker-v2.module.scss';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import type {RGBA} from '@Utils/color.util';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {ColorPickerLayout, DELAY_FOR_ON_CHANGE, PREVIEW_CIRCLE_SIZE} from './color-picker-v2.types';
import {ColorPickerLayoutContext} from './color-picker-layout-context';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {usePeekingScrollHeight} from '@Hooks/usePeekingScrollHeight';
import {SCROLL_PEEK_ITEM_SELECTOR} from '@Components/color-picker-v2/components/swatches/swatches.types';
import type {ColorPickerProps, ColorStorage} from './color-picker-v2.types';

export function ColorPicker({
  className = '',
  type = ColorFillPropertyType.SOLID,
  layout,
  isGradientEnabled = true,
  hideTypeSelector = false,
  onChangeColorPickerMode = (): void => {},
  addDelayToOnChangeColor = false,
  showSmallBrandDropdownHeader = false,
  showOnMyDesignsSection = true,
  showPicker = true,
  isAlphaDisabled = false,
  onColorSelectedFromBrands = (): void => {},
  ...props
}: ColorPickerProps): ReactElement<any> {
  const layoutFromContext = useContext(ColorPickerLayoutContext);
  const effectiveLayout = layout ?? layoutFromContext;
  const isPopupLayout = effectiveLayout === ColorPickerLayout.POPUP;
  const [selectedColorId, setSelectedColorId] = useState(props.color[0].id);
  const [tempColorValue, setTempColorValue] = useState(props.color);
  const timer = useRef<number | undefined>(undefined);
  const popupScrollBodyRef = useRef<HTMLDivElement>(null);
  const {topShadow: isPopupBodyScrolled} = useTopBottomScrollShadow(popupScrollBodyRef);
  const gradientSectionRef = useRef<HTMLDivElement>(null);

  const getSelectedColor = (): RGBA => {
    let currentColor = props.color;
    if (addDelayToOnChangeColor) {
      currentColor = tempColorValue;
    }
    if (type === ColorFillPropertyType.SOLID) {
      return currentColor[0].color;
    }

    for (let index = 0; index < props.color.length; index += 1) {
      if (currentColor[index].id === selectedColorId) {
        return currentColor[index].color;
      }
    }

    return currentColor[0].color;
  };

  const getIndexOfSelectedColor = (): number => {
    if (type === ColorFillPropertyType.SOLID) {
      return 0;
    }
    return props.color.findIndex((colorItem) => {
      return colorItem.id === selectedColorId;
    });
  };

  const prepareColorFromStringToObject = (value: RGBA | null): ColorStorage[] => {
    if (value === null) {
      return [];
    }
    const indexOfSelectedColor = getIndexOfSelectedColor();
    const newColorsArray = [...props.color];
    newColorsArray[indexOfSelectedColor].color = value;
    return newColorsArray;
  };

  const fireOnChange = (color: RGBA | null): void => {
    if (color === null) {
      return;
    }
    props.onImmediateChange?.();
    setTempColorValue(prepareColorFromStringToObject(color));
    if (!addDelayToOnChangeColor) {
      props.onChangeColor(prepareColorFromStringToObject(color));
      return;
    }

    if (timer) {
      clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      props.onChangeColor(prepareColorFromStringToObject(color));
    }, DELAY_FOR_ON_CHANGE);
  };

  const onChangeColor = (color: RGBA): void => {
    fireOnChange(color);
  };

  const fireOnChangeEnded = (colors?: ColorStorage[]): void => {
    clearTimeout(timer.current);
    props.onChangeEnded(colors);
  };

  const onColorSelectFromBrands = (brandColor: RGBA): void => {
    const newColorsArray = prepareColorFromStringToObject(brandColor);
    props.onChangeColor(newColorsArray);
    fireOnChangeEnded(newColorsArray);
    onColorSelectedFromBrands();
  };

  const onChangeTypeHandler = (newType: ColorFillPropertyType): void => {
    if (props.onChangeType) {
      props.onChangeType(newType);
    }
  };

  const getSelectedColorId = (): string => {
    if (type === ColorFillPropertyType.SOLID) {
      return props.color[0].id;
    }
    return selectedColorId;
  };

  const getPanelTypeSection = (): ReactElement => {
    return (
      <ColorOptionTypeSection
        type={type}
        color={props.color}
        selectedColorId={getSelectedColorId()}
        isGradientEnabled={isGradientEnabled}
        isColorMixed={props.isColorMixed}
        isTypeMixed={props.isTypeMixed}
        hideTypeSelector={hideTypeSelector}
        onChangeSelectedColor={setSelectedColorId}
        onChangeType={onChangeTypeHandler}
      />
    );
  };

  const isTypeSelectorShown = isGradientEnabled && !hideTypeSelector;

  const getPopupTypeTabs = (): ReactElement | null => {
    if (!isTypeSelectorShown) {
      return null;
    }
    return <ColorTypeTabs type={type} isTypeMixed={props.isTypeMixed ?? false} onChangeType={onChangeTypeHandler} />;
  };

  const isGradientSectionShown = isTypeSelectorShown && !props.isTypeMixed && type !== ColorFillPropertyType.SOLID;

  /** How far the palettes move when switching tab: down by the gradient section's height going to Gradient, up going back. */
  const getTabSwitchLayoutShift = (): number => {
    const gradientSectionHeight = gradientSectionRef.current?.getBoundingClientRect().height ?? 0;
    return isGradientSectionShown ? -gradientSectionHeight : gradientSectionHeight;
  };

  usePeekingScrollHeight({
    scrollContainerRef: popupScrollBodyRef,
    peekItemSelector: SCROLL_PEEK_ITEM_SELECTOR,
    remeasureKey: `${type}-${props.isTypeMixed ?? false}`,
    getAlternateLayoutShift: getTabSwitchLayoutShift,
  });

  /**
   * On the Solid tab the gradient section is still rendered, hidden and out of flow, so its height is known and the
   * popup can keep one height across both tabs.
   */
  const getPopupGradientSection = (): ReactElement | null => {
    if (!isTypeSelectorShown) {
      return getFixedTypeGradientStops();
    }
    return (
      <div
        ref={gradientSectionRef}
        className={isGradientSectionShown ? styles.gradientSectionContainer : styles.hiddenGradientSectionMeasure}
        aria-hidden={!isGradientSectionShown}
        inert={!isGradientSectionShown}
      >
        <GradientStyleSection
          type={type}
          color={props.color}
          selectedColorId={getSelectedColorId()}
          onChangeSelectedColor={setSelectedColorId}
          onChangeType={onChangeTypeHandler}
        />
      </div>
    );
  };

  /** With the type fixed by the caller (e.g. the page background), a gradient still needs its stops to pick which one to edit. */
  const getFixedTypeGradientStops = (): ReactElement | null => {
    if (!isGradientEnabled || props.isTypeMixed || type === ColorFillPropertyType.SOLID) {
      return null;
    }
    return (
      <div className="spacing-m-b-4">
        <GradientStopSwatches color={props.color} selectedColorId={getSelectedColorId()} onChangeSelectedColor={setSelectedColorId} />
      </div>
    );
  };

  const getSelectedColorPreview = (): ReactElement | null => {
    if (!isPopupLayout) {
      return null;
    }
    return (
      <ColorFill
        className={`${styles.selectedColorPreview} spacing-m-r-2`}
        color={getSelectedColor()}
        isColorMixed={props.isColorMixed}
        width={PREVIEW_CIRCLE_SIZE}
        height={PREVIEW_CIRCLE_SIZE}
        borderRadius={PREVIEW_CIRCLE_SIZE / 2}
        hasFocus={false}
      />
    );
  };

  const getColorSections = (): ReactElement => {
    return (
      <>
        <HexPicker
          className={isPopupLayout ? styles.popupHexPicker : ''}
          color={getSelectedColor()}
          onChange={onChangeColor}
          onChangeEnded={(value: RGBA): void => {
            fireOnChangeEnded(prepareColorFromStringToObject(value));
          }}
          onPointerDown={(): void => {
            props.onImmediateChange?.();
          }}
          isAlphaDisabled={isAlphaDisabled}
        />
        <InputSection
          className={isPopupLayout ? 'flex-items-center' : ''}
          onChangeColorPickerMode={(state): void => {
            onChangeColorPickerMode(state);
          }}
          color={getSelectedColor()}
          onChangeColor={(color): void => {
            fireOnChange(color);
          }}
          onChangeEnded={(color): void => {
            fireOnChangeEnded(prepareColorFromStringToObject(color));
          }}
          showPicker={showPicker}
          isAlphaDisabled={isAlphaDisabled}
          leadingContent={getSelectedColorPreview()}
          inputFieldSize={isPopupLayout ? InputFieldSize.XSMALL : undefined}
          hasCopyControl={isPopupLayout}
        />
        <BrandsSection
          className="spacing-p-t-6"
          onAddBrandAssetClicked={(): void => {
            if (props.onAddBrandAssetClicked) {
              props.onAddBrandAssetClicked();
            }
          }}
          showSmallDropdownHeader={showSmallBrandDropdownHeader}
          onColorSelect={onColorSelectFromBrands}
        />
        {showOnMyDesignsSection ? (
          <OnMyDesignSection
            onColorSelect={(color): void => {
              props.onChangeColor(prepareColorFromStringToObject(color as RGBA));
              fireOnChangeEnded(prepareColorFromStringToObject(color as RGBA));
            }}
            colorsOnDesign={props.onMyDesign}
          />
        ) : null}
        <DefaultPresetSection
          onChangeColor={(color): void => {
            props.onChangeColor(prepareColorFromStringToObject(color));
            fireOnChangeEnded(prepareColorFromStringToObject(color));
          }}
          isMobileVariant={false}
        />
      </>
    );
  };

  const getPopupBody = (): ReactElement => {
    return (
      <>
        {getPopupTypeTabs()}
        <div ref={popupScrollBodyRef} className={`${styles.popupScrollBody} ${isPopupBodyScrolled ? styles.scrolledPopupScrollBody : ''}`}>
          {getPopupGradientSection()}
          {getColorSections()}
        </div>
      </>
    );
  };

  const getPanelBody = (): ReactElement => {
    return (
      <>
        {getPanelTypeSection()}
        {getColorSections()}
      </>
    );
  };

  return (
    <ColorPickerLayoutContext.Provider value={effectiveLayout}>
      <div
        data-testid={PosterEditorTestId.COLOR_PICKER_PANEL}
        className={`${className} flex-v-row ${isPopupLayout ? styles.popupContainer : `spacing-p-l-5 spacing-p-r-5 ${styles.container}`}`}
      >
        {isPopupLayout ? getPopupBody() : getPanelBody()}
      </div>
    </ColorPickerLayoutContext.Provider>
  );
}
